// SNIGJ - Call Center Widget - Emergency Contact like 911
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Phone, MessageCircle, MapPin, Search, X, 
  Building2, Scale, Shield, Users, AlertTriangle,
  ExternalLink
} from 'lucide-react';
import api from '../lib/api';

// WhatsApp & Messenger icons
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const MessengerIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259 6.559-6.963 3.13 3.259 5.889-3.259-6.559 6.963z"/>
  </svg>
);

const CallCenterWidget = ({ isOpen, onClose }) => {
  const { language } = useAuth();
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadStructures();
    }
  }, [isOpen]);

  const loadStructures = async () => {
    setLoading(true);
    try {
      const response = await api.get('/structures', { params: { limit: 100 } });
      // Filter structures that have contact info
      const structuresWithContacts = (response.data.structures || response.data || []).filter(
        s => s.phone || s.whatsapp || s.email
      );
      
      // If no structures with contacts, use mock data for demo
      if (structuresWithContacts.length === 0) {
        setStructures(getMockStructures());
      } else {
        setStructures(structuresWithContacts);
      }
    } catch (error) {
      console.error('Failed to load structures:', error);
      // Use mock data on error
      setStructures(getMockStructures());
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demo - representative jurisdictions in DRC
  const getMockStructures = () => [
    {
      id: '1',
      name: 'Tribunal de Grande Instance de Kinshasa/Gombe',
      type: 'TRIBUNAL_GRANDE_INSTANCE',
      province: 'KINSHASA',
      phone: '+243 81 234 5678',
      whatsapp: '+243812345678',
      address: 'Avenue de la Justice, Gombe',
      lat: -4.3167,
      lng: 15.3000
    },
    {
      id: '2',
      name: 'Parquet Général de Kinshasa',
      type: 'PARQUET_GENERAL',
      province: 'KINSHASA',
      phone: '+243 81 987 6543',
      whatsapp: '+243819876543',
      address: 'Boulevard du 30 Juin, Gombe',
      lat: -4.3200,
      lng: 15.3100
    },
    {
      id: '3',
      name: 'Commissariat Central de Kinshasa',
      type: 'COMMISSARIAT',
      province: 'KINSHASA',
      phone: '+243 99 111 2222',
      whatsapp: '+243991112222',
      address: 'Avenue Kasa-Vubu, Kinshasa',
      lat: -4.3250,
      lng: 15.2900
    },
    {
      id: '4',
      name: 'Prison Centrale de Makala',
      type: 'PRISON',
      province: 'KINSHASA',
      phone: '+243 81 333 4444',
      address: 'Commune de Selembao',
      lat: -4.3700,
      lng: 15.2500
    },
    {
      id: '5',
      name: 'Tribunal de Paix de Lubumbashi',
      type: 'TRIBUNAL_PAIX',
      province: 'HAUT-KATANGA',
      phone: '+243 97 555 6666',
      whatsapp: '+243975556666',
      address: 'Centre-ville Lubumbashi',
      lat: -11.6600,
      lng: 27.4800
    },
    {
      id: '6',
      name: 'Cour d\'Appel de Lubumbashi',
      type: 'COUR_APPEL',
      province: 'HAUT-KATANGA',
      phone: '+243 97 777 8888',
      whatsapp: '+243977778888',
      address: 'Lubumbashi Centre',
      lat: -11.6650,
      lng: 27.4750
    },
    {
      id: '7',
      name: 'Tribunal de Commerce de Goma',
      type: 'TRIBUNAL_COMMERCE',
      province: 'NORD-KIVU',
      phone: '+243 99 888 9999',
      whatsapp: '+243998889999',
      address: 'Goma Centre',
      lat: -1.6786,
      lng: 29.2280
    }
  ];

  // Get unique provinces
  const provinces = ['all', ...new Set(structures.map(s => s.province).filter(Boolean))];

  // Filter structures
  const filteredStructures = structures.filter(s => {
    const matchesSearch = !searchQuery || 
      s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProvince = selectedProvince === 'all' || s.province === selectedProvince;
    return matchesSearch && matchesProvince;
  });

  // Get icon for structure type
  const getStructureIcon = (type) => {
    if (type?.includes('TRIBUNAL') || type?.includes('COUR')) return Scale;
    if (type?.includes('PARQUET')) return Building2;
    if (type?.includes('COMMISSARIAT') || type?.includes('POLICE')) return Shield;
    if (type?.includes('PRISON')) return Users;
    return Building2;
  };

  // Get color for structure type
  const getStructureColor = (type) => {
    if (type?.includes('TRIBUNAL') || type?.includes('COUR')) return 'text-blue-600 bg-blue-100';
    if (type?.includes('PARQUET')) return 'text-purple-600 bg-purple-100';
    if (type?.includes('COMMISSARIAT') || type?.includes('POLICE')) return 'text-orange-600 bg-orange-100';
    if (type?.includes('PRISON')) return 'text-red-600 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const handleCall = (phone) => {
    window.open(`tel:${phone.replace(/\s/g, '')}`, '_self');
  };

  const handleWhatsApp = (whatsapp) => {
    const number = whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/${number}`, '_blank');
  };

  const handleMessenger = (messengerId) => {
    window.open(`https://m.me/${messengerId}`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="block">
                {language === 'fr' ? 'Centre d\'Appel Judiciaire' : 'Judicial Call Center'}
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {language === 'fr' 
                  ? 'Contactez directement les juridictions - Service d\'urgence'
                  : 'Contact jurisdictions directly - Emergency service'}
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Emergency Banner */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-red-700 dark:text-red-400">
              {language === 'fr' ? 'Urgence Judiciaire' : 'Judicial Emergency'}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400">
              {language === 'fr' 
                ? 'Pour les cas urgents: arrestation arbitraire, violence, garde à vue prolongée'
                : 'For urgent cases: arbitrary arrest, violence, extended custody'}
            </p>
          </div>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-white gap-2"
            onClick={() => handleCall('+243 81 911 911')}
          >
            <Phone className="w-4 h-4" />
            911
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3 py-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={language === 'fr' ? 'Rechercher une juridiction...' : 'Search for a jurisdiction...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedProvince}
            onChange={(e) => setSelectedProvince(e.target.value)}
            className="px-3 py-2 border rounded-md bg-background min-w-[180px]"
          >
            <option value="all">{language === 'fr' ? 'Toutes les provinces' : 'All provinces'}</option>
            {provinces.filter(p => p !== 'all').map(province => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
        </div>

        {/* Structures List */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredStructures.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'fr' ? 'Aucune juridiction trouvée' : 'No jurisdiction found'}</p>
            </div>
          ) : (
            filteredStructures.map((structure) => {
              const Icon = getStructureIcon(structure.type);
              const colorClass = getStructureColor(structure.type);
              
              return (
                <Card key={structure.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-6 h-6" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base truncate">{structure.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {structure.province || 'N/A'}
                          </Badge>
                          {structure.address && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {structure.address}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Contact Buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {structure.phone && (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                            onClick={() => handleCall(structure.phone)}
                            data-testid={`call-${structure.id}`}
                          >
                            <Phone className="w-4 h-4" />
                            <span className="hidden sm:inline">
                              {language === 'fr' ? 'Appeler' : 'Call'}
                            </span>
                          </Button>
                        )}
                        {structure.whatsapp && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleWhatsApp(structure.whatsapp)}
                            data-testid={`whatsapp-${structure.id}`}
                          >
                            <WhatsAppIcon />
                          </Button>
                        )}
                        {structure.messenger && (
                          <Button
                            size="sm"
                            className="bg-blue-500 hover:bg-blue-600 text-white"
                            onClick={() => handleMessenger(structure.messenger)}
                            data-testid={`messenger-${structure.id}`}
                          >
                            <MessengerIcon />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="pt-3 border-t flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {filteredStructures.length} {language === 'fr' ? 'juridictions' : 'jurisdictions'}
          </p>
          <Button variant="outline" size="sm" onClick={() => window.open('/map', '_blank')} className="gap-2">
            <MapPin className="w-4 h-4" />
            {language === 'fr' ? 'Voir la carte' : 'View map'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallCenterWidget;
